import React, { useMemo } from 'react';
import { History, AlertTriangle, ArrowUp, ArrowDown, Minus, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const ChangeIndicator = ({ pct }) => {
  if (pct === null || pct === undefined) return <span className='text-muted-foreground'>—</span>;
  const abs = Math.abs(pct).toFixed(1);
  if (pct > 5) return <span className='text-danger flex items-center gap-1'><ArrowUp size={12} />+{abs}%</span>;
  if (pct < -5) return <span className='text-success flex items-center gap-1'><ArrowDown size={12} />-{abs}%</span>;
  return <span className='text-muted-foreground flex items-center gap-1'><Minus size={12} />{abs}%</span>;
};

/**
 * Year-on-year consistency comparison (Screen 19).
 * @param {object} props
 * @param {object} props.comparisonData - YoY comparison response
 */
const YoYConsistency = ({ comparisonData }) => {
  const data = comparisonData;

  if (!data) {
    return (
      <div className='flex flex-col gap-4'>
        <h2 className='text-lg font-semibold flex items-center gap-2'>
          <History size={20} strokeWidth={1.5} />
          Year-on-Year Consistency
        </h2>
        <Card className='bg-card'>
          <CardContent className='py-12 text-center'>
            <History size={40} className='mx-auto mb-3 text-muted-foreground' strokeWidth={1.5} />
            <p className='text-muted-foreground'>Select two reporting periods to compare.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between'>
        <h2 className='text-lg font-semibold flex items-center gap-2'>
          <History size={20} strokeWidth={1.5} />
          {data.period1} vs {data.period2}
        </h2>
        <div className='flex items-center gap-2'>
          <span className='text-sm text-muted-foreground'>Total Change:</span>
          <ChangeIndicator pct={data.totalEmissions?.change} />
        </div>
      </div>

      <div className='grid grid-cols-2 gap-4'>
        <Card className='bg-card'>
          <CardContent className='p-4 text-center'>
            <p className='text-xs text-muted-foreground'>{data.period1}</p>
            <p className='text-2xl font-bold'>{(data.totalEmissions?.period1 || 0).toFixed(1)}</p>
            <p className='text-xs text-muted-foreground'>tCO₂e</p>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4 text-center'>
            <p className='text-xs text-muted-foreground'>{data.period2}</p>
            <p className='text-2xl font-bold'>{(data.totalEmissions?.period2 || 0).toFixed(1)}</p>
            <p className='text-xs text-muted-foreground'>tCO₂e</p>
          </CardContent>
        </Card>
      </div>

      {data.byCategory?.length > 0 && (
        <Card className='bg-card'>
          <CardHeader>
            <CardTitle className='text-sm'>Category Comparison</CardTitle>
          </CardHeader>
          <CardContent className='p-0'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead className='text-right'>{data.period1}</TableHead>
                  <TableHead className='text-right'>{data.period2}</TableHead>
                  <TableHead className='text-right'>Change</TableHead>
                  <TableHead>Flags</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.byCategory.map((c) => (
                  <TableRow key={c.category}>
                    <TableCell className='text-sm font-medium'>{c.category}</TableCell>
                    <TableCell className='text-sm'>{c.scope}</TableCell>
                    <TableCell className='text-right font-mono text-sm'>
                      {c.period1 ? (c.period1.emissions || 0).toFixed(1) : '—'}
                    </TableCell>
                    <TableCell className='text-right font-mono text-sm'>
                      {c.period2 ? (c.period2.emissions || 0).toFixed(1) : '—'}
                    </TableCell>
                    <TableCell className='text-right'>
                      <ChangeIndicator pct={c.changePct} />
                    </TableCell>
                    <TableCell>
                      <div className='flex flex-wrap gap-1'>
                        {c.flags?.map((f, i) => (
                          <Badge
                            key={i}
                            className={
                              f.type === 'dropped_category'
                                ? 'bg-danger-bg text-danger text-xs'
                                : f.type === 'factor_change'
                                  ? 'bg-warning-bg text-warning text-xs'
                                  : 'bg-info-bg text-info text-xs'
                            }
                          >
                            {f.type.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {data.baseYearFlags?.length > 0 && (
        <Card className='border-warning bg-warning-bg/50'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 mb-2'>
              <AlertTriangle size={16} className='text-warning' strokeWidth={1.5} />
              <span className='text-sm font-semibold text-warning'>Base Year Recalculation Triggers</span>
            </div>
            <div className='flex flex-col gap-1'>
              {data.baseYearFlags.map((f, i) => (
                <p key={i} className='text-sm text-foreground'>{f.message}</p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {data.methodologyChanges?.length > 0 && (
        <Card className='bg-card'>
          <CardHeader>
            <CardTitle className='text-sm'>Methodology Changes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='flex flex-col gap-1'>
              {data.methodologyChanges.map((m, i) => (
                <p key={i} className='text-sm text-muted-foreground'>{m.message}</p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default YoYConsistency;
